import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Descriptions,
  DatePicker,
} from 'antd';
import {
  LinkOutlined,
  ReloadOutlined,
  PlusOutlined,
  EyeOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchPaymentLinks,
  fetchPaymentLinkDetail,
  createPaymentLink,
  updatePaymentLink,
  deletePaymentLink,
  activatePaymentLink,
  deactivatePaymentLink,
} from '../store/slices/paymentLinkSlice';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;

const PaymentLinksPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [filters, setFilters] = useState<any>({});

  const {
    paymentLinks,
    currentPaymentLink,
    creating,
    updating,
    error,
  } = useAppSelector((state) => state.paymentLink);

  useEffect(() => {
    dispatch(fetchPaymentLinks({ page: 1, limit: 20 }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleCreateLink = async (values: any) => {
    try {
      const amount = Math.round(parseFloat(values.amount) * 100);
      const data: any = {
        name: values.name,
        amount,
        currency: values.currency,
        description: values.description,
      };

      if (values.expires_at) {
        data.expires_at = values.expires_at.toISOString();
      }

      if (values.max_uses) {
        data.max_uses = values.max_uses;
      }

      await dispatch(createPaymentLink(data)).unwrap();
      message.success('支付链接已创建');
      setCreateModalVisible(false);
      form.resetFields();
      dispatch(fetchPaymentLinks({ page: 1, limit: 20 }));
    } catch (err: any) {
      message.error(err || '创建支付链接失败');
    }
  };

  const handleViewDetail = async (id: string) => {
    try {
      await dispatch(fetchPaymentLinkDetail(id)).unwrap();
      setDetailModalVisible(true);
    } catch (err: any) {
      message.error(err || '获取支付链接详情失败');
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    message.success('链接已复制到剪贴板');
  };

  const handleToggleActive = async (id: string, active: boolean) => {
    try {
      if (active) {
        await dispatch(deactivatePaymentLink(id)).unwrap();
        message.success('支付链接已停用');
      } else {
        await dispatch(activatePaymentLink(id)).unwrap();
        message.success('支付链接已激活');
      }
      dispatch(fetchPaymentLinks({ page: 1, limit: 20, filters }));
    } catch (err: any) {
      message.error(err || '操作失败');
    }
  };

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: '确认删除？',
      content: '删除后将无法恢复，确定要删除这个支付链接吗？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          await dispatch(deletePaymentLink(id)).unwrap();
          message.success('支付链接已删除');
          dispatch(fetchPaymentLinks({ page: 1, limit: 20, filters }));
        } catch (err: any) {
          message.error(err || '删除失败');
        }
      },
    });
  };

  const handleRefresh = () => {
    dispatch(fetchPaymentLinks({ page: 1, limit: 20, filters }));
  };

  const handleTableChange = (pagination: any, tableFilters: any) => {
    const newFilters: any = {};
    if (tableFilters.active && tableFilters.active.length > 0) {
      newFilters.active = tableFilters.active[0] === 'true';
    }
    setFilters(newFilters);
    dispatch(
      fetchPaymentLinks({
        page: pagination.current,
        limit: pagination.pageSize,
        filters: newFilters,
      })
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: '链接ID',
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => text || '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{ margin: 0, maxWidth: 200 }}
        >
          {text || '-'}
        </Paragraph>
      ),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Text strong>
          {record.currency} {(amount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      filters: [
        { text: '活跃', value: 'true' },
        { text: '停用', value: 'false' },
      ],
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? '活跃' : '停用'}
        </Tag>
      ),
    },
    {
      title: '使用次数',
      dataIndex: 'payment_count',
      key: 'payment_count',
      render: (count: number) => <Text>{count || 0}</Text>,
    },
    {
      title: '总收款',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number, record: any) => (
        <Text type="success">
          {record.currency} {((amount || 0) / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (text: string) => {
        if (!text) return <Text type="secondary">永久</Text>;
        const expiry = dayjs(text);
        const isExpired = expiry.isBefore(dayjs());
        return (
          <Text type={isExpired ? 'danger' : undefined}>
            {expiry.format('YYYY-MM-DD HH:mm:ss')}
          </Text>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 250,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record.id)}
          >
            详情
          </Button>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => handleCopyLink(record.url)}
          >
            复制
          </Button>
          <Button
            type="link"
            icon={record.active ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleActive(record.id, record.active)}
          >
            {record.active ? '停用' : '激活'}
          </Button>
          <Button
            type="link"
            danger
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const activeLinks = paymentLinks.data?.filter((link) => link.active).length || 0;
  const totalRevenue =
    paymentLinks.data?.reduce((sum, link) => sum + (link.total_amount || 0), 0) || 0;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          支付链接
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            创建支付链接
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总链接数"
              value={paymentLinks.meta?.total || 0}
              prefix={<LinkOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="活跃链接"
              value={activeLinks}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总收款金额"
              value={totalRevenue / 100}
              precision={2}
              suffix="USD"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={paymentLinks.data || []}
          loading={paymentLinks.loading}
          rowKey="id"
          locale={{ emptyText: '暂无数据' }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          pagination={{
            total: paymentLinks.meta?.total || 0,
            current: paymentLinks.meta?.current_page || 1,
            pageSize: paymentLinks.meta?.per_page || 20,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
        />
      </Card>

      {/* Create Modal */}
      <Modal
        title="创建支付链接"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateLink}>
          <Form.Item
            label="名称"
            name="name"
            rules={[{ required: true, message: '请输入支付链接名称' }]}
          >
            <Input placeholder="如：会员充值" />
          </Form.Item>

          <Form.Item
            label="金额"
            name="amount"
            rules={[
              { required: true, message: '请输入金额' },
              {
                validator: (_, value) => {
                  const amount = parseFloat(value);
                  if (isNaN(amount) || amount <= 0) {
                    return Promise.reject('请输入有效的金额');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入金额"
              min={0}
              step={0.01}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label="币种"
            name="currency"
            initialValue="USD"
            rules={[{ required: true, message: '请选择币种' }]}
          >
            <Select placeholder="请选择">
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
              <Select.Option value="GBP">GBP</Select.Option>
              <Select.Option value="CNY">CNY</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input.TextArea rows={3} placeholder="请输入支付链接描述" />
          </Form.Item>

          <Form.Item label="过期时间" name="expires_at">
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="选填，不设置则永久有效"
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Form.Item>

          <Form.Item label="最大使用次数" name="max_uses">
            <InputNumber
              style={{ width: '100%' }}
              placeholder="选填，不设置则无限制"
              min={1}
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  setCreateModalVisible(false);
                  form.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="支付链接详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button
            key="copy"
            icon={<CopyOutlined />}
            onClick={() => {
              if (currentPaymentLink) {
                handleCopyLink(currentPaymentLink.url);
              }
            }}
          >
            复制链接
          </Button>,
          <Button key="close" type="primary" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {currentPaymentLink && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="链接ID" span={2}>
              <Text copyable>{currentPaymentLink.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="支付链接" span={2}>
              <Paragraph
                copyable
                style={{
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  wordBreak: 'break-all',
                }}
              >
                {currentPaymentLink.url}
              </Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              {currentPaymentLink.description}
            </Descriptions.Item>
            <Descriptions.Item label="金额">
              <Text strong>
                {currentPaymentLink.currency}{' '}
                {(currentPaymentLink.amount / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={currentPaymentLink.active ? 'success' : 'default'}>
                {currentPaymentLink.active ? '活跃' : '停用'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="使用次数">
              {currentPaymentLink.payment_count || 0}
            </Descriptions.Item>
            <Descriptions.Item label="总收款金额">
              <Text type="success">
                {currentPaymentLink.currency}{' '}
                {((currentPaymentLink.total_amount || 0) / 100).toFixed(2)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(currentPaymentLink.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="过期时间">
              {currentPaymentLink.expires_at
                ? dayjs(currentPaymentLink.expires_at).format('YYYY-MM-DD HH:mm:ss')
                : '永久'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default PaymentLinksPage;
